/**
 * Consulting Intro Component
 * ScrollTrigger + Pin + Clip-path 마스크 애니메이션 + SplitType 텍스트 fill
 * 순서: 1. 제목 사라짐 → 2. 이미지 펼쳐짐 → 3. 오버레이 나타남 → 4. 텍스트 fill
 */

export class ConsultingIntro {
    constructor() {
        this.containers = document.querySelectorAll(".no-consulting-intro");
        if (!this.containers.length) return;

        // GSAP이 로드되었는지 확인
        if (typeof gsap === "undefined") {
            console.warn("GSAP not loaded");
            return;
        }

        // ScrollTrigger가 있으면 등록
        if (typeof ScrollTrigger !== "undefined") {
            gsap.registerPlugin(ScrollTrigger);
        }

        this.init();
    }

    init() {
        this.containers.forEach((container) => {
            const title = container.querySelector(".no-consulting-intro__title");
            const imageWrapper = container.querySelector(".no-consulting-intro__image");
            const image = imageWrapper?.querySelector("img");
            const overlay = imageWrapper?.querySelector(".no-consulting-intro__overlay");
            const textOverlay = overlay?.querySelector(".no-consulting-intro__text-overlay");
            const textDesc = textOverlay?.querySelector(".no-consulting-intro__text-desc");

            if (!title || !imageWrapper || !image || !overlay || !textOverlay || !textDesc) {
                console.warn("ConsultingIntro: Missing elements", { title, imageWrapper, image, overlay, textOverlay, textDesc });
                return;
            }

            // 초기 상태 설정
            // 이미지: 가로 80%만 보이도록 clip-path 설정 (중앙 정렬)
            // 아래쪽 반만 보이도록 설정 (50% ~ 100%)
            // title과 겹치지 않도록 이미지를 화면 하단에 배치
            gsap.set(image, {
                clipPath: "polygon(10% 50%, 90% 50%, 90% 100%, 10% 100%)", // 가로 80% (10%~90%), 아래쪽 반만 (50%~100%)
            });

            gsap.set(overlay, {
                opacity: 0,
            });

            // SplitType으로 텍스트 분리 (애니메이션 전에 미리 분리)
            let splitText = null;
            if (typeof SplitType !== "undefined") {
                try {
                    splitText = new SplitType(textDesc, {
                        types: 'lines,words,chars',
                        tagName: "span",
                    });
                    
                    // 각 문자 초기 상태 설정 - 명확하게 숨김
                    if (splitText.chars && splitText.chars.length > 0) {
                        gsap.set(splitText.chars, {
                            opacity: 0,
                            y: 20,
                        });
                    }
                } catch (error) {
                    console.warn("SplitType error:", error);
                    splitText = null;
                    // SplitType 실패 시 텍스트 전체를 숨김
                    gsap.set(textDesc, {
                        opacity: 0,
                        y: 20,
                    });
                }
            } else {
                // SplitType이 없을 때 텍스트 전체를 숨김
                gsap.set(textDesc, {
                    opacity: 0,
                    y: 20,
                });
            }

            // ScrollTrigger + Pin 애니메이션
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: container,
                    start: "top top",
                    end: "+=250%",
                    scrub: 1,
                    pin: true,
                    pinSpacing: true,
                    anticipatePin: 1,
                    markers: false,
                },
            });

            // 1. 제목이 위로 올라가면서 사라짐 (0.0 ~ 0.25)
            tl.to(title, {
                y: -100,
                opacity: 0,
                ease: "power3.in",
            }, 0);

            // 2. 이미지 clip-path로 아래에서 위로, 가로로도 풀로 펼쳐짐 (0.1 ~ 0.5)
            // 가로 80% → 100%, 세로 50% → 100%
            tl.to(image, {
                clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
                ease: "power3.inOut",
            }, 0.1);

            // 3. 오버레이 나타남 (0.5 ~ 0.65) - 이미지가 완전히 펼쳐진 후
            tl.to(overlay, {
                opacity: 1,
                ease: "power2.out",
            }, 0.5);

            // 4. 텍스트 fill 애니메이션 (0.6 ~ 1.0) - 오버레이가 나타난 후
            if (splitText && splitText.chars && splitText.chars.length > 0) {
                // SplitType으로 분리된 문자들 애니메이션 - 천천히 타이핑
                tl.to(splitText.chars, {
                    opacity: 1,
                    y: 0,
                    ease: "power2.out",
                    stagger: {
                        amount: 0.6, // 전체 stagger 시간을 늘려서 더 천천히
                        from: "start"
                    },
                }, 0.6);
                
                // 텍스트가 완전히 나타난 후 0.5초 대기
                tl.to({}, {
                });
                
                // 애니메이션 완료 후 최종 상태 보장 (timeline 끝에)
                tl.call(() => {
                    // 모든 문자가 확실히 보이도록
                    gsap.set(splitText.chars, {
                        opacity: 1,
                        y: 0,
                    });
                });
                
                // 마지막에는 텍스트가 사라지지 않고 그대로 유지 (빈 .to로 끝)
                tl.to({}, {    
                    duration: 0.1,
                });
            } else {
                // SplitType이 없을 때 폴백 - 텍스트 전체를 애니메이션
                console.warn("ConsultingIntro: SplitType not available, using fallback");
                tl.to(textDesc, {
                    opacity: 1,
                    y: 0,
                    ease: "power2.out",
                }, 0.6);
                
                // 텍스트가 완전히 나타난 후 0.5초 대기
                tl.to({}, {
                });
                
                // 애니메이션 완료 후 최종 상태 보장
                tl.call(() => {
                    gsap.set(textDesc, {
                        opacity: 1,
                        y: 0,
                    });
                });
                
                // 마지막에는 텍스트가 사라지지 않고 그대로 유지 (빈 .to로 끝)
                tl.to({}, {
                    duration: 0.1,
                });
            }
        });
    }
}
